import { Link } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import type { Lang } from "@/lib/i18n";

interface HeroProps {
  lang: Lang;
}

export function Hero({ lang }: HeroProps) {
  const { t } = useI18n();
  return (
    <section className="relative isolate overflow-hidden">
      {/* Ink night sky */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 110%, color-mix(in oklab, var(--navy) 70%, var(--ink)) 0%, var(--ink) 60%, var(--ink) 100%)",
        }}
      />
      {/* Rotating lighthouse beam */}
      <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
        <div className="beam" />
        <div
          className="beacon absolute left-1/2 top-1/2 size-6 md:size-5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_36px_10px_color-mix(in_oklab,var(--amber)_90%,transparent)] md:shadow-[0_0_28px_8px_color-mix(in_oklab,var(--amber)_80%,transparent)]"
          style={{ background: "var(--amber)" }}
        />
      </div>
      {/* Paper-grain fade at bottom */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 -z-10 h-24"
        style={{
          background:
            "linear-gradient(to bottom, transparent, var(--background))",
        }}
      />

      <div className="mx-auto max-w-3xl px-5 pb-28 pt-20 text-center sm:pt-28 md:pb-36 md:pt-36">
        <p
          className="mb-6 text-[11px] uppercase tracking-[0.22em]"
          style={{ color: "color-mix(in oklab, var(--amber) 85%, white)" }}
        >
          {t.hero_eyebrow}
        </p>
        <h1
          className="font-display text-4xl leading-[1.05] sm:text-5xl md:text-6xl"
          style={{ color: "var(--paper)" }}
        >
          {t.hero_title}
        </h1>
        <p
          className="mx-auto mt-6 max-w-xl text-base sm:text-lg"
          style={{ color: "color-mix(in oklab, var(--paper) 75%, transparent)" }}
        >
          {t.hero_sub}
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="#stories"
            className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium transition-transform hover:scale-[1.02]"
            style={{ background: "var(--amber)", color: "var(--ink)" }}
          >
            {t.hero_cta_read} →
          </a>
          <Link
            to="/$lang/submit"
            params={{ lang }}
            className="inline-flex items-center justify-center rounded-full border px-5 py-2.5 text-sm font-medium transition-colors"
            style={{
              borderColor: "color-mix(in oklab, var(--paper) 35%, transparent)",
              color: "var(--paper)",
            }}
          >
            {t.hero_cta_submit}
          </Link>
        </div>

        <p
          className="mt-6 text-xs"
          style={{ color: "color-mix(in oklab, var(--paper) 55%, transparent)" }}
        >
          {t.hero_note}
        </p>
      </div>
    </section>
  );
}